import { useCallback, useEffect, useState } from 'react';
import { Dialog } from '../../components/Dialog.tsx';
import { Icon } from '../../components/Icon.tsx';
import { useAddPhotos, usePhotoUrl } from './api.ts';
import { zoneErrorMessage } from './errors.ts';
import { resizeToJpeg } from './photoResize.ts';
import type { IssuePhotoRef } from './types.ts';

export const MAX_PHOTOS = 5;

function Thumb({ issueId, photo, index, onOpen }: { issueId: string; photo: IssuePhotoRef; index: number; onOpen: () => void }): JSX.Element {
  const url = usePhotoUrl(issueId, photo.id, true);
  return (
    <li>
      <button
        type="button"
        onClick={onOpen}
        aria-label={`Open photo ${index + 1}`}
        className="relative block aspect-square w-full overflow-hidden rounded-2xl border border-[hsl(var(--border)/0.7)] bg-[hsl(var(--surface-high))]"
      >
        {url && <img src={url} alt={`Photo ${index + 1}`} className="size-full object-cover" />}
      </button>
    </li>
  );
}

function LightboxImage({ issueId, photo, index }: { issueId: string; photo: IssuePhotoRef; index: number }): JSX.Element {
  const url = usePhotoUrl(issueId, photo.id, false);
  return (
    <div className="grid min-h-48 place-items-center">
      {url ? (
        <img src={url} alt={`Photo ${index + 1}`} className="max-h-[65vh] max-w-full rounded-xl object-contain" />
      ) : (
        <p className="status-label" role="status">Loading…</p>
      )}
    </div>
  );
}

export function PhotoGrid({
  issueId, photos, canAdd,
}: { issueId: string; photos: IssuePhotoRef[]; canAdd: boolean }): JSX.Element | null {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const add = useAddPhotos();

  const count = photos.length;
  const close = useCallback(() => setOpenIndex(null), []);
  const step = useCallback(
    (d: number) => setOpenIndex((i) => (i === null || count === 0 ? i : (i + d + count) % count)),
    [count],
  );

  useEffect(() => {
    if (openIndex === null) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'ArrowLeft') step(-1);
      else if (e.key === 'ArrowRight') step(1);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [openIndex, step]);

  const addFiles = async (files: FileList | null): Promise<void> => {
    if (!files || files.length === 0) return;
    setError(null);
    const picked = Array.from(files).slice(0, Math.max(0, MAX_PHOTOS - count));
    if (files.length > picked.length) setError(`You can attach up to ${MAX_PHOTOS} photos.`);
    setProcessing(true);
    try {
      const form = new FormData();
      for (const [i, f] of picked.entries()) form.append('photos', await resizeToJpeg(f), `photo-${i + 1}.jpg`);
      await add.mutateAsync({ id: issueId, form });
    } catch (err) {
      setError(zoneErrorMessage(err));
    } finally {
      setProcessing(false);
    }
  };

  const showAdd = canAdd && count < MAX_PHOTOS;
  if (count === 0 && !showAdd) return null;
  const current = openIndex === null ? null : photos[openIndex];

  return (
    <section className="space-y-3" aria-label="Photos">
      <h2 className="text-sm font-medium text-primary">Photos <span className="font-normal text-muted-foreground">({count}/{MAX_PHOTOS})</span></h2>
      {count > 0 && (
        <ul className="grid grid-cols-3 gap-2 md:grid-cols-5">
          {photos.map((p, i) => <Thumb key={p.id} issueId={issueId} photo={p} index={i} onOpen={() => setOpenIndex(i)} />)}
        </ul>
      )}
      {showAdd && (
        <label className={`btn-secondary cursor-pointer ${processing || add.isPending ? 'pointer-events-none opacity-50' : ''}`}>
          <Icon name="add_a_photo" size={18} /> {processing || add.isPending ? 'Uploading…' : 'Add photos'}
          <input
            type="file"
            accept="image/*"
            multiple
            className="sr-only"
            onChange={(e) => {
              const input = e.currentTarget;
              void addFiles(input.files).finally(() => { input.value = ''; });
            }}
          />
        </label>
      )}
      {error && <p className="text-sm text-destructive" role="alert">{error}</p>}

      <Dialog open={current !== null && current !== undefined} onClose={close} title={`Photo ${(openIndex ?? 0) + 1} of ${count}`}>
        {current && openIndex !== null && (
          <div className="space-y-3">
            <LightboxImage key={current.id} issueId={issueId} photo={current} index={openIndex} />
            {count > 1 && (
              <div className="flex items-center justify-between">
                <button type="button" className="btn-ghost btn-icon" aria-label="Previous photo" onClick={() => step(-1)}>
                  <Icon name="chevron_left" size={24} />
                </button>
                <span className="text-sm text-muted-foreground">{openIndex + 1} / {count}</span>
                <button type="button" className="btn-ghost btn-icon" aria-label="Next photo" onClick={() => step(1)}>
                  <Icon name="chevron_right" size={24} />
                </button>
              </div>
            )}
          </div>
        )}
      </Dialog>
    </section>
  );
}
